import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router";
import type { SiteNode } from "@zirro/schema";
import type { Product, PublicStore } from "../../../lib/api";

/**
 * Everything storefront components need: the published store, its products,
 * and links within the published site (/s/:slug/...).
 */
export interface Storefront {
  store: PublicStore;
  slug: string;
  /**
   * False in the editor and preview: products render for real, but links,
   * the cart and the wishlist don't act, so designing never leaves the page.
   */
  interactive: boolean;
  /**
   * Set in the editor for a real store: opens the product form for a product,
   * or for a new one when given null. Product cards then show an Edit button.
   */
  onEditProduct?: (product: Product | null) => void;
  paths: {
    home: string;
    shop: string;
    product: (product: Pick<Product, "slug">) => string;
    checkout: string;
    wishlist: string;
    order: (orderId: string, token: string) => string;
  };
  go: (path: string) => void;
  findProduct: (productSlug: string) => Product | undefined;
}

const StorefrontContext = createContext<Storefront | null>(null);

export function StorefrontProvider({
  store,
  interactive = true,
  basePath,
  onGo,
  onEditProduct,
  children,
}: {
  store: PublicStore;
  interactive?: boolean;
  basePath?: string;
  onGo?: (path: string) => void;
  onEditProduct?: (product: Product | null) => void;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const value = useMemo<Storefront>(() => {
    const base = basePath ?? `/s/${store.slug}`;
    return {
      store,
      slug: store.slug,
      interactive,
      onEditProduct,
      paths: {
        home: base,
        shop: `${base}/shop`,
        product: (product) => `${base}/products/${product.slug}`,
        checkout: `${base}/checkout`,
        wishlist: `${base}/wishlist`,
        order: (orderId, token) => `${base}/order/${orderId}?token=${encodeURIComponent(token)}`,
      },
      go: (path) => {
        if (!interactive) return;
        if (onGo) {
          onGo(path);
        } else {
          navigate(path);
          window.scrollTo({ top: 0 });
        }
      },
      findProduct: (productSlug) => store.products.find((p) => p.slug === productSlug),
    };
  }, [store, interactive, basePath, onGo, onEditProduct, navigate]);
  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
}

export function useStorefrontContext(): Storefront {
  const value = useContext(StorefrontContext);
  if (!value) throw new Error("useStorefrontContext must be used inside StorefrontProvider");
  return value;
}

/**
 * Store slots: containers in a store template whose ids end with these
 * suffixes are filled with live products on the published site. In the
 * editor their own children remain as an editable placeholder.
 */
export const PRODUCTS_SLOT = "__products";
export const CATALOG_SLOT = "__catalog";

export function slotKind(node: SiteNode): "products" | "catalog" | null {
  if (node.id.endsWith(PRODUCTS_SLOT)) return "products";
  if (node.id.endsWith(CATALOG_SLOT)) return "catalog";
  return null;
}

/** Whether any node on a page is a catalog slot. */
export function hasCatalogSlot(nodes: SiteNode[]): boolean {
  return nodes.some(
    (node) => slotKind(node) === "catalog" || (node.children ? hasCatalogSlot(node.children) : false),
  );
}
